// src/pages/SignupSuccess.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import GradientButton from '../components/GradientButton';

// How long to wait for the Stripe webhook to activate the account before
// showing the "success but still activating" screen.
const ACTIVATION_WAIT_MS = 60 * 1000;

const SignupSuccess = () => {
  const navigate = useNavigate();

// State
  const [showConfirm, setShowConfirm] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  // Auth/account-activation state. The account is created disabled and
  // enabled by the Stripe webhook, so there can be a delay after redirect.
  // 'idle' → normal success page, 'waiting' → "setting up your account…",
  // 'pending' → payment received but account not usable yet.
  const [authUser, setAuthUser] = useState(null);
  const [activation, setActivation] = useState('idle');

  // Track Firebase auth state for the whole life of this page so the
  // "Go to My Profile" flow knows whether the member is already signed in.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => setAuthUser(user));
    return () => unsubscribe();
  }, []);

  // While "waiting", give the webhook up to ACTIVATION_WAIT_MS to finish.
  // The signup password was scrubbed before the Stripe redirect (see
  // SignupStepThree), so we cannot sign the user in automatically — we can
  // only watch for an existing session; otherwise show the pending screen.
  useEffect(() => {
    if (activation !== 'waiting') return;
    if (authUser) {
      navigate('/profile');
      return;
    }
    const timerId = setTimeout(() => setActivation('pending'), ACTIVATION_WAIT_MS);
    return () => clearTimeout(timerId);
  }, [activation, authUser, navigate]);

// Google Analytics
useEffect(() => {
  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-X0CG0XBMDR';
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', 'G-X0CG0XBMDR');
}, []);

// Vibe Purchase conversion (fires on signup success page load)
useEffect(() => {
  // Get plan from sessionStorage (stored during signup; localStorage fallback for legacy flows)
let planPrice = '14.99'; // Default to Jackpot Chaser
  try {
    const signupData = sessionStorage.getItem('lottoSignupData') || localStorage.getItem('lottoSignupData');
    if (signupData) {
      const data = JSON.parse(signupData);
      if (data.plan === 'weeklyPlayer') {
        planPrice = '34.99';
      }
    }
  } catch (e) {
    console.log('Could not parse signup data, defaulting to Jackpot Chaser price');
  }

  /* eslint-disable no-unused-expressions, no-sequences, new-parens */
  !function(v,i,b,e,c,o){if(!v[c]){var s=v[c]=function(){s.process?s.process.apply(s,arguments):s.queue.push(arguments)};s.queue=[],s.b=1*new Date;var t=i.createElement(b);t.async=!0,t.src=e;var n=i.getElementsByTagName(b)[0];n.parentNode.insertBefore(t,n)}}(window,document,"script","https://s.vibe.co/vbpx.js","vbpx");
  /* eslint-enable no-unused-expressions, no-sequences, new-parens */
  window.vbpx('init','5E1q8x');
  window.vbpx('event','purchase', {'price_usd': planPrice});
  console.log(`✅ Vibe purchase conversion fired with price: $${planPrice}`);
}, []);

  // Cleanup ALL temp signup data AFTER conversion tracking.
  // Clear from BOTH sessionStorage (current flow) and localStorage (legacy
  // flows that stored the password/email there before this fix).
  useEffect(() => {
    try {
      // Keep the signup email around (in state only) for the contact link
      setContactEmail(
        sessionStorage.getItem('signupEmail') ||
        localStorage.getItem('signupEmail') ||
        ''
      );
    } catch {}
    const signupKeys = [
      'lottoSignupData',
      'signupEmail',
      'signupPassword',
      'userIP',
      'userAgent',
      'termsAcceptedAt',
      'termsSnapshot'
    ];
    try {
      signupKeys.forEach((key) => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
    } catch {}
  }, []);

  // Handlers
  const openConfirm = () => setShowConfirm(true);
  const goContact = () => {
    const qs = new URLSearchParams({
      reason: 'missing_numbers',
      email: contactEmail
    });
    navigate(`/contact?${qs.toString()}`);
  };

  return (
    <>
      {/* Shake keyframes for the yellow pill (modal) */}
      <style>{`
        @keyframes llShake {
          0% { transform: translateX(0) rotate(0deg); }
          15% { transform: translateX(-2px) rotate(-0.5deg); }
          30% { transform: translateX(2px) rotate(0.5deg); }
          45% { transform: translateX(-2px) rotate(-0.5deg); }
          60% { transform: translateX(2px) rotate(0.5deg); }
          75% { transform: translateX(-1px) rotate(-0.25deg); }
          100% { transform: translateX(0) rotate(0deg); }
        }
        @keyframes llSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Success Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #A6CE3A 0%, #8BB62A 100%)',
          borderRadius: 16,
          padding: '2rem 1rem',
          textAlign: 'center',
          margin: '1rem',
          boxShadow: '0 8px 32px rgba(166, 206, 58, 0.3)'
        }}
      >
        <h1
          style={{
            margin: '0 0 1rem 0',
            color: '#FFF',
            fontWeight: 800,
            fontSize: 'clamp(2rem, 7vw, 3rem)',
            textShadow: '2px 2px rgba(0,0,0,0.2)',
            lineHeight: 1.2
          }}
        >
          🎉 Welcome to LuckyLogic.ai!
        </h1>
        <p
          style={{
            margin: 0,
            color: '#F0FDF4',
            fontSize: 'clamp(1rem, 3.5vw, 1.125rem)'
          }}
        >
          Congratulations and thank you for subscribing!
        </p>
      </div>

      {/* Content Card */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '1rem 1rem 2rem 1rem'
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: 16,
            padding: '2rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.06)',
            width: '100%',
            maxWidth: 920
          }}
        >
          <div
            style={{
              backgroundColor: '#f8fafc',
              padding: '1.5rem',
              borderRadius: 12,
              marginBottom: '2rem',
              textAlign: 'left',
              border: '1px solid #e5e7eb'
            }}
          >
            <h3
              style={{
                color: '#1f2937',
                fontSize: '1.1rem',
                marginBottom: '1rem',
                fontWeight: 700
              }}
            >
              What happens next:
            </h3>
            <ul
              style={{
                paddingLeft: '1.25rem',
                lineHeight: 1.8,
                color: '#4b5563',
                margin: 0
              }}
            >
              <li>🎲 You’ll receive your lottery numbers before each draw</li>
              <li>📱 Numbers sent via your preferred method (text/email)</li>
              <li>🎟️ Numbers sent for your preferred games (Powerball / Mega Millions)</li>
              <li>🛠️ Change communication, games, and subscription preferences in your Profile</li>
            </ul>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            <GradientButton
              variant="blueCompact"
              onClick={openConfirm}
              hoverStyle={{
                transform: 'translateY(-3px) scale(1.02)',
                boxShadow: '0 12px 35px rgba(30,159,242,0.6), 0 6px 18px rgba(0,0,0,0.2)'
              }}
              restStyle={{
                boxShadow: '0 8px 25px rgba(30,159,242,0.5), 0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              Go to My Profile →
            </GradientButton>
          </div>

          <p
            style={{
              fontSize: '0.9rem',
              color: '#6b7280',
              margin: 0,
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            Questions? Contact us at support@LuckyLogic.ai
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000
          }}
        >
          <div
  style={{
    background: '#fff',
    width: '100%',
    maxWidth: 720,
    borderRadius: 16,
    boxShadow: '0 24px 60px rgba(0,0,0,.25)',
    border: '1px solid rgba(0,0,0,.06)',
    padding: '1.5rem',
    textAlign: 'center',
    position: 'relative',
    overflow: 'visible'         // <— add this
  }}
>

            <h3
              style={{
                margin: '0 0 1rem 0',
                fontWeight: 800,
                fontSize: 'clamp(1.2rem, 3.5vw, 1.6rem)',
                color: '#16a34a'
              }}
            >
              📩 You should have received 2 emails & 2 texts with your first numbers.
            </h3>

            {/* Yellow warning pill */}
            <div
              style={{
  display: 'inline-block',
  padding: '0.75rem 1.25rem',
  background: '#fde047',
  border: '1px solid #f59e0b',
  borderRadius: 9999,
  boxShadow: '0 16px 34px rgba(245, 158, 11, 0.25)',
  margin: '0 0 0.9rem 0',
  fontWeight: 800,
  color: '#0f172a',
  animation: 'llShake 0.9s ease-in-out infinite both',
  willChange: 'transform',
  maxWidth: '95%',
  wordWrap: 'break-word',
  lineHeight: '1.3',
  textAlign: 'center'
}}
            >
              ⚠️ CHECK YOUR SPAM / PROMOTIONS FOLDER FIRST
            </div>

            <p
  style={{
    margin: '0.35rem auto 1.1rem auto',
    color: '#334155',
    fontSize: '1.05rem',
    fontWeight: 600,
    lineHeight: 1.55,
    textAlign: 'center',
    maxWidth: '90%'
  }}
>
  IMPORTANT - If our email lands in your Spam/Junk folder, drag it to your Inbox or click the “Not Spam” button. You may need to do this a few times for it to take.
</p>

            {/* Buttons row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}
            >
 <button
 type="button"
onClick={() => {
  setShowConfirm(false);
  if (authUser) {
    // Fast path: already signed in — straight to the profile.
    navigate('/profile');
  } else {
    // Slow path: the Stripe webhook may still be activating the account.
    // Show an explicit "setting up your account" state instead of silently
    // dumping the member on /login with no explanation.
    setActivation('waiting');
  }
}}
  style={{
    padding: '1rem 1.5rem',
    minWidth: 260,
    border: 'none',
    borderRadius: 16,
    cursor: 'pointer',
    color: '#fff',
    fontWeight: 800,
    fontSize: '1.15rem',
    letterSpacing: 0.2,
    background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)',
    boxShadow:
      '0 10px 28px rgba(30,159,242,0.40), 0 6px 14px rgba(0,0,0,0.15)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease'
  }}
  onMouseOver={(e) => {
    e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
    e.currentTarget.style.boxShadow =
      '0 14px 36px rgba(30,159,242,0.55), 0 8px 18px rgba(0,0,0,0.20)';
    e.currentTarget.style.background =
      'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)';
  }}
  onMouseOut={(e) => {
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.boxShadow =
      '0 10px 28px rgba(30,159,242,0.40), 0 6px 14px rgba(0,0,0,0.15)';
    e.currentTarget.style.background =
      'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)';
  }}
>
  Got my email &amp; text
</button>


              <button
  onClick={goContact}
  style={{
    padding: '1rem 1.5rem',
    background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', // orange → orange-red
    color: '#fff',
    fontWeight: 800,
    fontSize: '1.0rem',
    border: 'none',                 // important: avoid hover clipping
    borderRadius: '12px',           // consistent radius
    cursor: 'pointer',
    boxShadow: '0 8px 22px rgba(249, 115, 22, 0.35), 0 4px 12px rgba(0,0,0,0.12)',
    transition: 'all 0.3s ease',    // smooth hover
    minHeight: 48,
    minWidth: 240,
    letterSpacing: 0.3

  }}
  onMouseOver={(e) => {
    e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
    e.currentTarget.style.boxShadow =
      '0 12px 32px rgba(249,115,22,0.45), 0 6px 18px rgba(0,0,0,0.2)';
    e.currentTarget.style.background =
      'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
  }}
  onMouseOut={(e) => {
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.boxShadow =
      '0 8px 22px rgba(249, 115, 22, 0.35), 0 4px 12px rgba(0,0,0,0.12)';
    e.currentTarget.style.background =
      'linear-gradient(135deg, #fb923c 0%, #f97316 100%)';
  }}
>
  Did NOT receive my email/text
</button>

            </div>
          </div>
        </div>
      )}

      {/* Account activation: waiting overlay (webhook may still be running) */}
      {activation === 'waiting' && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: '#fff',
              width: '100%',
              maxWidth: 560,
              borderRadius: 16,
              boxShadow: '0 24px 60px rgba(0,0,0,.25)',
              border: '1px solid rgba(0,0,0,.06)',
              padding: '2rem 1.5rem',
              textAlign: 'center'
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 48,
                height: 48,
                margin: '0 auto 1.25rem auto',
                border: '5px solid #e5e7eb',
                borderTopColor: '#A6CE3A',
                borderRadius: '50%',
                animation: 'llSpin 0.9s linear infinite'
              }}
            />
            <h3
              style={{
                margin: '0 0 0.75rem 0',
                fontWeight: 800,
                fontSize: 'clamp(1.15rem, 3.5vw, 1.45rem)',
                color: '#16a34a'
              }}
            >
              ✅ Payment received — setting up your account…
            </h3>
            <p
              style={{
                margin: '0 0 1.25rem 0',
                color: '#334155',
                fontSize: '1rem',
                lineHeight: 1.55
              }}
            >
              We're activating your membership now. This usually takes a few
              seconds, but can take up to a minute.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                padding: '0.85rem 1.5rem',
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)',
                boxShadow: '0 8px 22px rgba(30,159,242,0.35), 0 4px 12px rgba(0,0,0,0.12)',
                minHeight: 48
              }}
            >
              Continue to Sign In
            </button>
          </div>
        </div>
      )}

      {/* Account activation: payment received but account still pending */}
      {activation === 'pending' && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: '#fff',
              width: '100%',
              maxWidth: 640,
              borderRadius: 16,
              boxShadow: '0 24px 60px rgba(0,0,0,.25)',
              border: '1px solid rgba(0,0,0,.06)',
              padding: '2rem 1.5rem',
              textAlign: 'center'
            }}
          >
            <h3
              style={{
                margin: '0 0 0.75rem 0',
                fontWeight: 800,
                fontSize: 'clamp(1.15rem, 3.5vw, 1.45rem)',
                color: '#16a34a'
              }}
            >
              ✅ Your payment was received and your account is being activated.
            </h3>
            <p
              style={{
                margin: '0 0 1.25rem 0',
                color: '#334155',
                fontSize: '1rem',
                lineHeight: 1.6,
                maxWidth: '92%',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Sign in with the email and password you chose during signup.
              If you can't sign in within a few minutes, use Forgot Password
              or contact us — your payment is safe.
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}
            >
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{
                  padding: '0.95rem 1.5rem',
                  minWidth: 200,
                  border: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)',
                  boxShadow: '0 8px 22px rgba(30,159,242,0.35), 0 4px 12px rgba(0,0,0,0.12)',
                  minHeight: 48
                }}
              >
                Go to Sign In
              </button>
              <button
                type="button"
                onClick={() => {
                  const qs = new URLSearchParams({
                    reason: 'account_pending',
                    email: contactEmail
                  });
                  navigate(`/contact?${qs.toString()}`);
                }}
                style={{
                  padding: '0.95rem 1.5rem',
                  minWidth: 200,
                  border: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
                  boxShadow: '0 8px 22px rgba(249, 115, 22, 0.35), 0 4px 12px rgba(0,0,0,0.12)',
                  minHeight: 48
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignupSuccess;
